import { useRequest } from '@/api/request'
import MiiaFwConfig from '@/configs/config'
import { ERROR_CODES } from '@/constants/error/errorCode'
import type {
  CompleteMultipartUploadParam,
  CompleteMultipartUploadResponse,
  CreateMultipartUploadParam,
  CreateMultipartUploadResponse,
  GetPresignedUrlParam,
  GetPresignedUrlResponse,
  UploadChunkWithProgressResponse
} from '@/types/api/dup/fileUpload/fileUploadApi'
import type { ErrorCode } from '@/utils/globalError/entities/valueObjects/types'
import { GlobalError } from '@/utils/globalError/GlobalError'

export const useFileUploadApi = () => {
  const { request } = useRequest()
  /** XHRを管理するMap */
  const xhrMap = new Map<string, XMLHttpRequest>()

  /** ファイルアップロードAPIへリクエストする */
  const requestFileUploadApi = <T, U>(func: string, arg: T) => {
    return request<T, U>(MiiaFwConfig.config.apiGwEndpointDup, '/fileUpload', func, arg)
  }

  /**
   * ファイルアップロードAPI createMultipartUploadを実行する
   * @param param リクエストパラメータ
   * @returns レスポンス
   */
  const createMultipartUpload = async (param: CreateMultipartUploadParam) => {
    const res = await requestFileUploadApi<
      CreateMultipartUploadParam,
      CreateMultipartUploadResponse
    >('createMultipartUpload', param)
    if ('error' in res) {
      throw new GlobalError(
        [ERROR_CODES._811010_API_EXECUTION_FAILED, res.error.errorCode as ErrorCode],
        { message: res.error.errorMessage }
      )
    }
    return res.result
  }

  /**
   * ファイルアップロードAPI getPresignedUrlを実行する
   * @param param リクエストパラメータ
   * @returns レスポンス
   */
  const getPresignedUrl = async (param: GetPresignedUrlParam) => {
    const res = await requestFileUploadApi<GetPresignedUrlParam, GetPresignedUrlResponse>(
      'getPresignedUrl',
      param
    )
    if ('error' in res) {
      throw new GlobalError(
        [ERROR_CODES._811010_API_EXECUTION_FAILED, res.error.errorCode as ErrorCode],
        { message: res.error.errorMessage }
      )
    }
    return res.result
  }

  /**
   * ファイルアップロードAPI completeMultipartUploadを実行する
   * @param param リクエストパラメータ
   * @returns レスポンス
   */
  const completeMultipartUpload = async (param: CompleteMultipartUploadParam) => {
    const res = await requestFileUploadApi<
      CompleteMultipartUploadParam,
      CompleteMultipartUploadResponse
    >('completeMultipartUpload', param)
    if ('error' in res) {
      throw new GlobalError(
        [ERROR_CODES._811010_API_EXECUTION_FAILED, res.error.errorCode as ErrorCode],
        { message: res.error.errorMessage }
      )
    }
    return res.result
  }

  /**
   * 進捗率のコールバック付きバイナリデータアップロードを実行する
   * @param url アップロード先URL
   * @param chunk アップロードデータ
   * @param onProgress アップロード進捗コールバック
   * @param fileKey ファイルキー
   * @returns チャンクアップロード返却値
   */
  const uploadChunkWithProgress = (
    url: string,
    chunk: Blob,
    onProgress: (loaded: number, total: number) => void,
    fileKey: string
  ) => {
    return new Promise<UploadChunkWithProgressResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhrMap.set(fileKey, xhr)

      xhr.open('PUT', url)
      xhr.setRequestHeader('Content-Type', 'application/octet-stream')

      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total)
        }
      }

      xhr.onload = () => {
        if (xhr.status < 200 || 300 <= xhr.status) {
          reject(new GlobalError(ERROR_CODES._815001_S3_PUT_HTTP_STATUS_ERROR))
          return
        }
        const etag = xhr.getResponseHeader('Etag')
        if (etag == null) {
          reject(new GlobalError(ERROR_CODES._815002_S3_PUT_NOT_FOUND_ETAG_ERROR))
          return
        }
        resolve({ etag: etag })
      }

      xhr.onerror = () => reject(new GlobalError(ERROR_CODES._811010_API_EXECUTION_FAILED))

      xhr.send(chunk)
    })
  }

  /**
   * バイナリデータアップロードを中断する
   * @param fileKey ファイルキー
   */
  const cancelChunkUpload = (fileKey: string) => {
    xhrMap.get(fileKey)?.abort()
  }

  return {
    createMultipartUpload,
    getPresignedUrl,
    completeMultipartUpload,
    uploadChunkWithProgress,
    cancelChunkUpload
  }
}
